// components/common/QRScannerDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  QrCodeScanner,
  Edit,
} from '@mui/icons-material';

interface QRScannerDialogProps {
  open: boolean;
  onClose: () => void;
  onScan: (result: string) => void;
}

const QRScannerDialog: React.FC<QRScannerDialogProps> = ({
  open,
  onClose,
  onScan,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [manualCode, setManualCode] = useState('');
  const [scannerError, setScannerError] = useState<string | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setScannerError(null);
  };

  const handleManualSubmit = () => {
    if (manualCode.trim()) {
      onScan(manualCode.trim());
      setManualCode('');
    }
  };

  const handleScannerResult = (result: string) => {
    onScan(result);
  };

  // Symulacja skanera - w prawdziwej aplikacji użyjemy react-qr-reader
  const handleMockScan = () => {
    // Mock scan result
    const mockResult = 'OBJ_123_QR_CODE';
    handleScannerResult(mockResult);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Skanuj kod QR/NFC
      </DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab icon={<QrCodeScanner />} label="Skanuj" />
            <Tab icon={<Edit />} label="Wpisz ręcznie" />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {scannerError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {scannerError}
              </Alert>
            ) : (
              <>
                <Box
                  sx={{
                    width: '100%',
                    height: 200,
                    backgroundColor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    border: '2px dashed',
                    borderColor: 'grey.400',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Kamera będzie tutaj<br />
                    (Na razie mock)
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  onClick={handleMockScan}
                  startIcon={<QrCodeScanner />}
                >
                  Symuluj skan (demo)
                </Button>
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Wyceluj kamerę w kod QR lub tag NFC
                </Typography>
              </>
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ py: 2 }}>
            <TextField
              fullWidth
              label="Kod QR / NFC"
              placeholder="Wpisz lub wklej kod"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleManualSubmit();
                }
              }}
              helperText="Możesz wpisać kod ręcznie lub wkleić ze schowka"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Anuluj
        </Button>
        {activeTab === 1 && (
          <Button
            onClick={handleManualSubmit}
            variant="contained"
            disabled={!manualCode.trim()}
          >
            Przejdź do obiektu
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QRScannerDialog;